<template>
  <BannerCard :withHint="false" type="property-info" :isActionable="isCancelledSubscription">
    <template #cardData>
      <div class="tw:flex tw:gap-x-4">
        <div
          class="tw:rounded-md tw:bg-center tw:bg-cover tw:p-1 tw:border tw:flex-shrink-0 tw:max-h-[70px] tw:w-[62px] tw:h-[60px]"
          :class="isCancelledSubscription ? 'tw:border-orange-800' : 'tw:border-green-700'"
        >
          <div v-if="isImageLoading" class="tw:w-full tw:h-full tw:rounded-md tw:bg-gray-300 tw:animate-pulse" />
          <img
            v-show="!isImageLoading"
            :src="src"
            alt="hotel-main-image"
            class="tw:rounded-md tw:w-full tw:h-full tw:object-cover"
            @load="isImageLoading = false"
            @error="isImageLoading = false"
          />
        </div>
        <div class="tw:flex tw:flex-col tw:justify-between tw:flex-1">
          <h3 class="tw:text-xl tw:font-bold">{{ currentAccommodation?.name }}</h3>
          <div class="tw:flex tw:md:flex-row tw:flex-col tw:md:items-center tw:gap-2">
            <span
              class="tw:order-1 tw:md:order-0 tw:text-sm tw:font-bold tw:py-1 tw:px-2 tw:self-start tw:rounded"
              :class="
                isCancelledSubscription
                  ? 'tw:text-orange-800 tw:bg-orange-200'
                  : 'tw:text-green-700 tw:bg-green-200'
              "
              data-testid="plus-status-label"
              >{{ $t(statusLabel) }}</span
            >
            <p v-if="isCancelledSubscription" class="tw:text-xs tw:text-gray-700">
              <i18n-t keypath="business_studio_plus_active_until_date" tag="p">
                <template #ActiveDate>
                  <SmallDotLoader v-if="!studioPlusCampaign" />
                  <b v-else>{{ formatDate(studioPlusCampaign.endDate) }}</b>
                </template>
              </i18n-t>
            </p>
          </div>
        </div>
      </div>
    </template>
    <template #dropdownMenu="{ closeMenu }">
      <div
        class="tw:px-4 tw:py-3 tw:text-sm tw:hover:bg-blue-200 tw:hover:text-blue-800 tw:rounded-lg"
        @click="openReactivation(closeMenu)"
      >
        {{ $t('business_studio_plus_reactivate_cta') }}
      </div>
    </template>
  </BannerCard>
</template>
<script setup>
import { computed, ref, watch } from 'vue'

import SmallDotLoader from '@/components/base/loaders/SmallDotLoader.vue'
import BannerCard from '@/components/studioPlus/insights/BannerCard.vue'

import { useSessionValues } from '@/composables/useSessionValues'
import { selectedAccommodationId, useAccommodationsByOrgId } from '@/layouts/queries'
import { useStudioPlusCampaign } from '@/components/payment/queries'
import { useStudioPlusReactivation } from '@/components/billing/premiumProducts/reactivation/useStudioPlusReactivation'
import { useProToPlusReactivation } from '@/components/billing/premiumProducts/reactivation/useProToPlusReactivation'
import { useFormatting } from '@/composables/useFormatting'

import appAnalytics from '@/utils/tracking'

const isImageLoading = ref(true)
const { formatDate } = useFormatting()
const { selectedOrganisationId } = useSessionValues()

const { currentAccommodation } = useAccommodationsByOrgId(selectedOrganisationId)
const { isCancelledSubscription, isPro, studioPlusCampaign } = useStudioPlusCampaign(selectedAccommodationId)
const { openReactivationModal } = useStudioPlusReactivation()
const { openProToPlusModal } = useProToPlusReactivation()

const src = computed(() => {
  const path = currentAccommodation.value?.mainImageUrls?.imagePath || 'dummy.jpeg'
  return `https://imgcy.trivago.com/c_fill,d_dummy.jpeg,e_sharpen:60,f_auto,h_60,q_auto,w_62/${path}`
})

const statusLabel = computed(() => {
  if (isCancelledSubscription.value) return 'plus_subscription_expiring'
  else return 'subscription_status_active'
})

const openReactivation = closeMenu => {
  if (!selectedAccommodationId.value) return

  if (isPro.value) {
    appAnalytics.track(appAnalytics.events.PRO_TO_PLUS_MODAL_OPENED, {
      item_id: selectedAccommodationId.value,
      source: 'business-studio-insights-property-info-card',
    })
    openProToPlusModal(selectedAccommodationId.value, 'business-studio-insights-property-info-card')
  } else {
    appAnalytics.track(appAnalytics.events.SP_PLUS_INSIGHTS_STATUS_INDICATOR_CLICKED, {
      item_id: selectedAccommodationId.value,
      menu_type: 'reactivation',
    })
    openReactivationModal(selectedAccommodationId, 'business-studio-insights-property-info-card')
  }
  closeMenu?.()
}

watch(src, () => {
  isImageLoading.value = true
})
</script>
